import { ChangeEvent, ReactNode } from "react";

import { Colors, fontMedium, fontXSmall } from "~foundations";

import { TextAreaInput, type TextAreaInputProps } from "../TextAreaInput";

/********* Types ********/
type CheckboxInputProps = {
  label: ReactNode;

  /** [Optional] Description displayed below the label. */
  description?: string;
  /** @deprecated Use `description` instead. */
  descriptionText?: string;
  descriptionTextState?: "primary" | "secondary";

  /**
   * [Optional] Renders a container around the component to emphasize it.
   * @default false
   */
  hasContainer?: boolean;

  isChecked?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;

  hasInputField?: boolean;
  textAreaProps?: TextAreaInputProps;
  hasBorderBottom?: boolean;

  /** Callback when the checkbox changes */
  onChange?: (isChecked: boolean) => void;
  /** @deprecated Use `onChange` instead. */
  onCheckedChange?: (isChecked: boolean) => void;
};

/**
 * Component | Checkbox
 *
 * A checkbox component with optional label, description, and input fields.
 *
 * Note: Hover states are handled in `Checkbox.css`, so any wrapper
 * or parent components may need their own hover styles
 * if additional styling is required.
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=11456-2763&m
 */
export function CheckboxInput(props: CheckboxInputProps) {
  const {
    label,
    description = props.descriptionText,
    descriptionTextState = "primary",
    hasContainer = false,
    isDisabled,
    isChecked,
    isReadOnly,
    hasInputField = false,
    hasBorderBottom = false,
    textAreaProps,
    onChange = props.onCheckedChange,
  } = props;

  /*** Handlers ***/
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isDisabled && !isReadOnly) onChange?.(e.target.checked);
  };

  /*** Render ***/
  return (
    <label
      css={{
        // Layout
        display: "flex",
        justifyContent: "flex-start",
        gap: 16,
        padding: "16px 24px",
        alignItems: "start",

        // Background & Hover
        transition: "background-color 200ms ease",
        backgroundColor: Colors.white200,
        ":hover": !isDisabled && !isReadOnly ? { background: Colors.white300 } : {},

        // Text
        ...fontMedium({ color: isDisabled ? "slate300" : "black" }),

        // Border
        borderBottom: hasBorderBottom ? `1px solid ${Colors.slate200}` : "none",

        cursor: isDisabled || isReadOnly ? "not-allowed" : "pointer",

        // Border Emphasis
        ...(hasContainer && {
          border: `1px solid ${Colors.blue500}`,
          borderRadius: 8,
        }),
      }}
    >
      {/* Hidden input for accessibility */}
      <input
        data-testid={
          typeof label === "string"
            ? `checkbox-${label.toLowerCase().replace(/ /g, "-")}`
            : "checkbox"
        }
        type="checkbox"
        checked={isChecked}
        onChange={handleOnChange}
        disabled={isDisabled}
        readOnly={isReadOnly}
        css={{ opacity: 0, position: "absolute" }}
      />
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "inherit",
          height: 24,
          width: 24,
          flexShrink: 0,
        }}
      >
        <CheckIcon {...props}></CheckIcon>
      </div>

      <div css={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
        <>
          {label}
          {description && (
            <p
              css={{
                ...fontXSmall({
                  color: descriptionTextState === "secondary" ? "slate400" : "black",
                }),
                margin: 0,
              }}
            >
              {description}
            </p>
          )}
        </>

        {hasInputField && isChecked && textAreaProps && (
          <div css={{ paddingTop: 16 }}>
            <TextAreaInput {...textAreaProps} />
          </div>
        )}
      </div>
    </label>
  );
}

/********* Helper Components ********/
function CheckIcon(props: CheckboxInputProps) {
  const { isDisabled, isChecked, isReadOnly } = props;

  /*** Constants ***/
  const ICON_SIZE = 18;
  const borderColor = isDisabled ? Colors.slate300 : isReadOnly ? Colors.black : Colors.blue500;
  const backgroundColor = isDisabled
    ? isChecked
      ? Colors.slate300
      : Colors.white200
    : isChecked
      ? isReadOnly
        ? Colors.black
        : Colors.blue500
      : "transparent";

  /*** Render ***/
  return (
    <svg
      viewBox="0 0 24 24"
      css={{
        width: ICON_SIZE,
        height: ICON_SIZE,
        position: "relative",
        transition: "background 180ms cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: `calc(${ICON_SIZE}px * 0.125)`,
        border: `2px solid ${borderColor}`,
        background: backgroundColor,
        boxSizing: "border-box",
      }}
    >
      <path
        css={{
          fill: "none",
          stroke: Colors.white200,
          strokeWidth: "3",
          opacity: isChecked ? "1" : "0",
          strokeDasharray: "30",
          strokeDashoffset: isChecked ? "0" : "30",
          transition: "stroke-dashoffset 90ms linear 90ms",
        }}
        d="M1.73,12.91 8.1,19.28 22.79,4.59"
      />
    </svg>
  );
}
